// Settings -> Material & Service Management -> Materials tab.
//
// Every material in the catalogue is listed here — not just the ones with a preference already
// set — since "which supplier/manufacturer wins" is a decision that applies to EVERY material,
// whether the tenant has overridden it yet or not (an unset one just falls back to lowest cost).
// Editing a row upserts its tenant_material_preference; there is no separate "add" step.
//
// The material list itself is server-searched/paginated with a user-adjustable page size — the
// catalogue can run into the hundreds. The preference rows a tenant HAS set are small, so they're
// loaded whole and joined client-side against the current page of materials.
import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useListMaterialPreferencesQuery,
  useUpsertMaterialPreferenceMutation,
  useDeleteMaterialPreferenceMutation,
  useSearchMaterialOptionsQuery,
  useSearchManufacturerOptionsQuery,
  useListCustomMaterialsQuery,
  useUpsertCustomMaterialMutation,
  useDeleteCustomMaterialMutation,
  type MaterialPreference,
  type SelectionMethod,
} from '@/redux/internal-services/tenantPreferencesApi';
import {
  useGetInventoryFacetsQuery,
  useGetEffectiveVariantPricesQuery,
  useCreateInventoryLineMutation,
  useUpdateInventoryLineMutation,
  type EffectiveVariantPrice,
} from '@/redux/internal-services/inventoryApi';
import { apiErrorMessage } from '@/utils/apiError';
import { Button } from '@/components/UI/Button';
import { Badge } from '@/components/UI/Badge';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { SearchInput } from '@/components/UI/SearchInput';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import SearchableSelect from '@/components/UI/SearchableSelect';
import Modal from '@/components/UI/Modal';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const FIELD_CLASS = fieldClass({ size: 'sm' });

const SELECTION_METHODS: SelectionMethod[] = [
  'PREFERRED_SUPPLIER',
  'PREFERRED_MANUFACTURER',
  'LOWEST_COST',
  'SPECIFIC_PRODUCT',
  'MANUAL',
];

type Toast = { type: 'success' | 'error'; msg: string };

const PreferenceForm: React.FC<{
  materialCode: string;
  materialName: string;
  initial?: MaterialPreference;
  manufacturerOptions: { manufacturer_code: string; manufacturer_name: string }[];
  supplierOptions: { supplier_code: string; supplier_name: string }[];
  onClose: () => void;
  onToast: (toast: Toast) => void;
}> = ({ materialCode, materialName, initial, manufacturerOptions, supplierOptions, onClose, onToast }) => {
  const { t } = useTranslation();
  const [upsert, { isLoading }] = useUpsertMaterialPreferenceMutation();
  const [manufacturerCode, setManufacturerCode] = useState<string | null>(initial?.preferred_manufacturer_code ?? null);
  const [supplierCode, setSupplierCode] = useState<string | null>(initial?.preferred_supplier_code ?? null);
  const [selectionMethod, setSelectionMethod] = useState<SelectionMethod>(
    initial?.selection_method ?? 'PREFERRED_SUPPLIER'
  );

  const save = async () => {
    try {
      await upsert({
        material_code: materialCode,
        preferred_manufacturer_code: manufacturerCode || undefined,
        preferred_supplier_code: supplierCode || undefined,
        selection_method: selectionMethod,
      }).unwrap();
      onToast({ type: 'success', msg: t('settings.materialService.materials.saved', 'Preference saved.') });
      onClose();
    } catch (error) {
      onToast({
        type: 'error',
        msg: apiErrorMessage(error, t, t('settings.materialService.materials.saveFailed', 'That preference could not be saved.')),
      });
    }
  };

  return (
    <tr className="bg-ui-subtle">
      <td colSpan={5} className="p-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-ui-text">
              {t('settings.materialService.materials.material', 'Material')}
            </label>
            <input type="text" value={materialName} disabled className={FIELD_CLASS} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ui-text">
              {t('settings.materialService.materials.manufacturer', 'Preferred manufacturer')}
            </label>
            <SearchableSelect
              value={manufacturerCode}
              onChange={setManufacturerCode}
              options={manufacturerOptions.map((m) => ({ value: m.manufacturer_code, label: m.manufacturer_name }))}
              placeholder={t('settings.materialService.materials.searchManufacturer', 'Search manufacturers…')}
              ariaLabel={t('settings.materialService.materials.manufacturer', 'Preferred manufacturer')}
              allowClear
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ui-text">
              {t('settings.materialService.materials.supplier', 'Preferred supplier')}
            </label>
            <SearchableSelect
              value={supplierCode}
              onChange={setSupplierCode}
              options={supplierOptions.map((s) => ({ value: s.supplier_code, label: s.supplier_name }))}
              placeholder={t('settings.materialService.materials.searchSupplier', 'Search suppliers…')}
              ariaLabel={t('settings.materialService.materials.supplier', 'Preferred supplier')}
              allowClear
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ui-text">
              {t('settings.materialService.materials.selectionMethod', 'Selection method')}
            </label>
            <select
              value={selectionMethod}
              onChange={(e) => setSelectionMethod(e.target.value as SelectionMethod)}
              className={FIELD_CLASS}
            >
              {SELECTION_METHODS.map((m) => (
                <option key={m} value={m}>
                  {t(`settings.materialService.materials.selectionMethods.${m}`, m)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button size="sm" loading={isLoading} onClick={save}>
            {t('common.save', 'Save')}
          </Button>
        </div>
      </td>
    </tr>
  );
};

const money = (value?: number | null, currency = 'USD') =>
  value === null || value === undefined
    ? '—'
    : new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);

const PriceSourceBadge: React.FC<{ source: EffectiveVariantPrice['price_source'] }> = ({ source }) => {
  const { t } = useTranslation();
  if (source === 'NONE') return null;
  const isTenant = source === 'TENANT_SUPPLIER';
  return (
    <Badge variant={isTenant ? 'accent' : 'neutral'} className="ml-2 uppercase">
      {isTenant
        ? t('settings.materialService.materials.tenantPriceBadge', 'Your price')
        : t('settings.materialService.materials.platformPriceBadge', 'Platform default')}
    </Badge>
  );
};

// Inline edit for one variant's price, inside the pricing modal. TENANT_SUPPLIER rows PATCH their
// existing supplier_product_master row; PLATFORM_SUPPLIER rows FORK — create_line makes a new
// tenant-owned row, leaving the platform default untouched for every other tenant.
const VariantPriceForm: React.FC<{
  materialCode: string;
  variant: EffectiveVariantPrice;
  onClose: () => void;
  onToast: (toast: Toast) => void;
}> = ({ materialCode, variant, onClose, onToast }) => {
  const { t } = useTranslation();
  const [createLine, { isLoading: creating }] = useCreateInventoryLineMutation();
  const [updateLine, { isLoading: updating }] = useUpdateInventoryLineMutation();
  const [price, setPrice] = useState(String(variant.unit_cost ?? ''));
  const [priceUom, setPriceUom] = useState(variant.purchase_uom ?? '');
  const [supplierName, setSupplierName] = useState(
    variant.price_source === 'TENANT_SUPPLIER' ? variant.supplier_code ?? '' : ''
  );
  const isFork = variant.price_source !== 'TENANT_SUPPLIER';

  const save = async () => {
    const value = Number(price);
    if (Number.isNaN(value)) return;
    try {
      if (isFork) {
        if (!supplierName.trim()) return;
        await createLine({
          supplier_name: supplierName.trim(),
          description: variant.variant_name,
          product_variant_code: variant.product_variant_code,
          material_code: materialCode,
          price: value,
          price_uom: priceUom || undefined,
        }).unwrap();
      } else if (variant.supplier_product_code) {
        await updateLine({ code: variant.supplier_product_code, changes: { price: value, price_uom: priceUom || undefined } }).unwrap();
      }
      onToast({ type: 'success', msg: t('settings.materialService.materials.priceSaved', 'Price saved.') });
      onClose();
    } catch (error) {
      onToast({
        type: 'error',
        msg: apiErrorMessage(error, t, t('settings.materialService.materials.priceSaveFailed', 'That price could not be saved.')),
      });
    }
  };

  return (
    <tr className="bg-ui-subtle">
      <td colSpan={4} className="p-3">
        {isFork && (
          <p className="mb-3 text-xs text-ui-text2">
            {t(
              'settings.materialService.materials.priceForkNotice',
              'Saving creates YOUR OWN price for this variant — the platform default is unaffected.'
            )}
          </p>
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {isFork && (
            <div>
              <label className="mb-1 block text-xs font-medium text-ui-text">
                {t('settings.materialService.materials.pricingSupplier', 'Supplier')}
              </label>
              <input type="text" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} className={FIELD_CLASS} autoFocus />
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-medium text-ui-text">
              {t('settings.materialService.materials.pricingPrice', 'Price')}
            </label>
            <input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className={FIELD_CLASS} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ui-text">
              {t('settings.materialService.serviceRates.uom', 'Unit')}
            </label>
            <input type="text" value={priceUom} onChange={(e) => setPriceUom(e.target.value)} className={FIELD_CLASS} />
          </div>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button size="sm" loading={creating || updating} onClick={save} disabled={!price || (isFork && !supplierName.trim())}>
            {t('common.save', 'Save')}
          </Button>
        </div>
      </td>
    </tr>
  );
};

// Pop-up over Inventory's effective pricing (app/domain/inventory) filtered to one material — so
// seeing AND editing "what do we actually pay for this" never requires leaving the Materials tab.
const MaterialPricingModal: React.FC<{
  materialCode: string;
  materialName: string;
  onClose: () => void;
  onToast: (toast: Toast) => void;
}> = ({ materialCode, materialName, onClose, onToast }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetEffectiveVariantPricesQuery(materialCode);
  const variants = data ?? [];
  const [editingVariant, setEditingVariant] = useState<string | null>(null);

  return (
    <Modal
      title={t('settings.materialService.materials.pricingModalTitle', '{{name}} — pricing', { name: materialName })}
      onClose={onClose}
      maxWidthClassName="max-w-2xl"
    >
      {isLoading && <LoadingState label={t('common.loading', 'Loading…')} className="py-6" />}
      {!isLoading && variants.length === 0 && (
        <EmptyState
          variant="inline"
          title={t('settings.materialService.materials.noPricing', 'No priced variant exists for this material yet — check the platform product catalogue.')}
        />
      )}
      {variants.length > 0 && (
        <div className="table-card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                <th className="th-cell">{t('settings.materialService.materials.pricingVariant', 'Variant')}</th>
                <th className="th-cell">{t('settings.materialService.materials.pricingPrice', 'Price')}</th>
                <th className="th-cell" />
                <th className="th-cell" />
              </tr>
            </thead>
            <tbody>
              {variants.map((variant) =>
                editingVariant === variant.product_variant_code ? (
                  <VariantPriceForm
                    key={variant.product_variant_code}
                    materialCode={materialCode}
                    variant={variant}
                    onClose={() => setEditingVariant(null)}
                    onToast={onToast}
                  />
                ) : (
                  <tr key={variant.product_variant_code}>
                    <td className="td-cell">{variant.variant_name}</td>
                    <td className="td-cell">
                      {money(variant.unit_cost, variant.currency)}
                      {variant.purchase_uom ? ` /${variant.purchase_uom}` : ''}
                    </td>
                    <td className="td-cell">
                      <PriceSourceBadge source={variant.price_source} />
                    </td>
                    <td className="td-cell text-right">
                      <Button type="button" variant="soft" size="xs" onClick={() => setEditingVariant(variant.product_variant_code)}>
                        {t('common.edit', 'Edit')}
                      </Button>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
};

// Inline "Add material" row — a tenant's own material for something the platform catalogue
// doesn't carry. Appears above the table, not as a modal, so it reads as adding a row to the
// same list rather than a separate flow.
const AddMaterialForm: React.FC<{ onClose: () => void; onToast: (toast: Toast) => void }> = ({ onClose, onToast }) => {
  const { t } = useTranslation();
  const [upsert, { isLoading }] = useUpsertCustomMaterialMutation();
  const [name, setName] = useState('');
  const [baseUom, setBaseUom] = useState('');

  const save = async () => {
    if (!name.trim()) return;
    try {
      await upsert({ material_name: name.trim(), base_uom: baseUom || undefined }).unwrap();
      onToast({ type: 'success', msg: t('settings.materialService.materials.materialSaved', 'Material added.') });
      onClose();
    } catch (error) {
      onToast({
        type: 'error',
        msg: apiErrorMessage(error, t, t('settings.materialService.materials.materialSaveFailed', 'That material could not be added.')),
      });
    }
  };

  return (
    <tr className="bg-ui-subtle">
      <td colSpan={5} className="p-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-ui-text">
              {t('settings.materialService.materials.materialName', 'Material name')}
            </label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={FIELD_CLASS} autoFocus />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ui-text">
              {t('settings.materialService.materials.materialUom', 'Base unit (optional)')}
            </label>
            <input type="text" value={baseUom} onChange={(e) => setBaseUom(e.target.value)} className={FIELD_CLASS} placeholder="SF, EA, LF…" />
          </div>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button size="sm" loading={isLoading} onClick={save} disabled={!name.trim()}>
            {t('common.save', 'Save')}
          </Button>
        </div>
      </td>
    </tr>
  );
};

const MaterialsPreferenceSection: React.FC<{ onToast: (toast: Toast) => void }> = ({ onToast }) => {
  const { t } = useTranslation();
  const [pricingModalFor, setPricingModalFor] = useState<{ code: string; name: string } | null>(null);
  const [addingMaterial, setAddingMaterial] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[1]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // The catalogue page being browsed — every PLATFORM material, searched/paginated server-side.
  const { data: materialsPage, isLoading, isFetching } = useSearchMaterialOptionsQuery({
    search: search || undefined,
    skip: page * pageSize,
    limit: pageSize,
  });
  // The tenant's OWN materials — small by nature (self-authored), so loaded whole rather than
  // paginated, and shown pinned above the platform catalogue page rather than folded into its
  // count: two independently-paginated sources can't share one "total" without either becoming
  // approximate, and pinning them keeps them from scrolling out of view on a busy catalogue.
  const { data: customMaterialsData } = useListCustomMaterialsQuery({ search: search || undefined, limit: 200 });
  const [deleteCustomMaterial] = useDeleteCustomMaterialMutation();
  // Every preference the tenant has actually set — small by nature (one row per overridden
  // material), loaded whole and joined client-side against whichever materials page is showing.
  const { data: preferencesData } = useListMaterialPreferencesQuery({ limit: 1000 });
  const { data: manufacturerLookup } = useSearchManufacturerOptionsQuery({ limit: 500 });
  const { data: facets } = useGetInventoryFacetsQuery();
  const [deletePreference] = useDeleteMaterialPreferenceMutation();
  const [editingMaterialCode, setEditingMaterialCode] = useState<string | null>(null);

  const customMaterials = customMaterialsData?.items ?? [];
  const materials = materialsPage?.items ?? [];
  const total = materialsPage?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);

  const preferenceByMaterial = useMemo(
    () => Object.fromEntries((preferencesData?.items ?? []).map((p) => [p.material_code, p])),
    [preferencesData]
  );
  const manufacturerOptions = useMemo(() => manufacturerLookup?.items ?? [], [manufacturerLookup]);
  const manufacturerNameByCode = useMemo(
    () => Object.fromEntries(manufacturerOptions.map((m) => [m.manufacturer_code, m.manufacturer_name])),
    [manufacturerOptions]
  );
  const supplierOptions = useMemo(() => facets?.suppliers ?? [], [facets]);
  const supplierNameByCode = useMemo(
    () => Object.fromEntries(supplierOptions.map((s) => [s.supplier_code, s.supplier_name])),
    [supplierOptions]
  );

  const remove = async (code: string) => {
    if (!window.confirm(t('settings.materialService.materials.confirmDelete', 'Remove this preference?'))) return;
    try {
      await deletePreference(code).unwrap();
      onToast({ type: 'success', msg: t('settings.materialService.materials.deleted', 'Preference removed.') });
    } catch (error) {
      onToast({
        type: 'error',
        msg: apiErrorMessage(error, t, t('settings.materialService.materials.deleteFailed', 'That preference could not be removed.')),
      });
    }
  };

  const removeCustomMaterial = async (code: string) => {
    if (!window.confirm(t('settings.materialService.materials.confirmDeleteMaterial', 'Delete this material? Any preference set on it is removed too.'))) return;
    try {
      const pref = preferenceByMaterial[code];
      if (pref) await deletePreference(pref.tenant_material_preference_code).unwrap();
      await deleteCustomMaterial(code).unwrap();
      onToast({ type: 'success', msg: t('settings.materialService.materials.materialDeleted', 'Material deleted.') });
    } catch (error) {
      onToast({
        type: 'error',
        msg: apiErrorMessage(error, t, t('settings.materialService.materials.materialDeleteFailed', 'That material could not be deleted.')),
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ui-text">
            {t('settings.materialService.materials.title', 'Preferred supplier & manufacturer')}
          </h3>
          <p className="mt-1 text-xs text-ui-text2">
            {t(
              'settings.materialService.materials.description',
              'Which supplier and manufacturer an estimate should prefer for each material. Negotiated prices are set under the Inventory tab.'
            )}
          </p>
        </div>
        <Button size="sm" onClick={() => setAddingMaterial(true)} disabled={addingMaterial}>
          {t('settings.materialService.materials.addMaterial', 'Add material')}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder={t('settings.materialService.materials.searchMaterial', 'Search materials…')}
          wrapperClassName="min-w-[240px] max-w-none flex-1"
        />
        <label className="flex items-center gap-2 text-xs text-ui-text2">
          {t('settings.materialService.rowsPerPage', 'Rows per page')}
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="table-card overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr>
              <th className="th-cell">{t('settings.materialService.materials.material', 'Material')}</th>
              <th className="th-cell">{t('settings.materialService.materials.manufacturer', 'Preferred manufacturer')}</th>
              <th className="th-cell">{t('settings.materialService.materials.supplier', 'Preferred supplier')}</th>
              <th className="th-cell">{t('settings.materialService.materials.selectionMethod', 'Selection method')}</th>
              <th className="th-cell" />
            </tr>
          </thead>
          <tbody>
            {addingMaterial && <AddMaterialForm onClose={() => setAddingMaterial(false)} onToast={onToast} />}
            {isLoading && (
              <tr>
                <td colSpan={5}>
                  <LoadingState label={t('common.loading', 'Loading…')} className="py-6" />
                </td>
              </tr>
            )}
            {!isLoading && materials.length === 0 && customMaterials.length === 0 && !addingMaterial && (
              <tr>
                <td colSpan={5}>
                  <EmptyState title={t('common.noResults', 'No matches.')} className="py-6" />
                </td>
              </tr>
            )}
            {customMaterials.map((material) => {
              const pref = preferenceByMaterial[material.material_code];
              if (editingMaterialCode === material.material_code) {
                return (
                  <PreferenceForm
                    key={material.material_code}
                    materialCode={material.material_code}
                    materialName={material.material_name}
                    initial={pref}
                    manufacturerOptions={manufacturerOptions}
                    supplierOptions={supplierOptions}
                    onClose={() => setEditingMaterialCode(null)}
                    onToast={onToast}
                  />
                );
              }
              return (
                <tr key={material.material_code} className="bg-ui-subtle">
                  <td className="td-cell">
                    {material.material_name}
                    <Badge className="ml-2 uppercase">
                      {t('settings.materialService.materials.customBadge', 'Custom')}
                    </Badge>
                  </td>
                  <td className="td-cell text-ui-text2">
                    {pref?.preferred_manufacturer_code
                      ? manufacturerNameByCode[pref.preferred_manufacturer_code] || pref.preferred_manufacturer_code
                      : '—'}
                  </td>
                  <td className="td-cell text-ui-text2">
                    {pref?.preferred_supplier_code
                      ? supplierNameByCode[pref.preferred_supplier_code] || pref.preferred_supplier_code
                      : '—'}
                  </td>
                  <td className="td-cell text-ui-text2">
                    {pref
                      ? t(`settings.materialService.materials.selectionMethods.${pref.selection_method}`, pref.selection_method)
                      : t('settings.materialService.materials.selectionMethods.LOWEST_COST', 'Lowest cost')}
                  </td>
                  <td className="td-cell text-right">
                    <Button type="button" variant="soft" size="xs" onClick={() => setPricingModalFor({ code: material.material_code, name: material.material_name })}>
                      {t('settings.materialService.materials.viewPricing', 'View pricing')}
                    </Button>
                    <Button type="button" variant="soft" size="xs" onClick={() => setEditingMaterialCode(material.material_code)} className="ml-1">
                      {t('common.edit', 'Edit')}
                    </Button>
                    <Button type="button" variant="destructive-ghost" size="xs" onClick={() => void removeCustomMaterial(material.material_code)} className="ml-1">
                      {t('settings.materialService.materials.deleteMaterial', 'Delete material')}
                    </Button>
                  </td>
                </tr>
              );
            })}
            {materials.map((material) => {
              const pref = preferenceByMaterial[material.material_code];
              if (editingMaterialCode === material.material_code) {
                return (
                  <PreferenceForm
                    key={material.material_code}
                    materialCode={material.material_code}
                    materialName={material.material_name}
                    initial={pref}
                    manufacturerOptions={manufacturerOptions}
                    supplierOptions={supplierOptions}
                    onClose={() => setEditingMaterialCode(null)}
                    onToast={onToast}
                  />
                );
              }
              return (
                <tr key={material.material_code}>
                  <td className="td-cell">{material.material_name}</td>
                  <td className="td-cell text-ui-text2">
                    {pref?.preferred_manufacturer_code
                      ? manufacturerNameByCode[pref.preferred_manufacturer_code] || pref.preferred_manufacturer_code
                      : '—'}
                  </td>
                  <td className="td-cell text-ui-text2">
                    {pref?.preferred_supplier_code
                      ? supplierNameByCode[pref.preferred_supplier_code] || pref.preferred_supplier_code
                      : '—'}
                  </td>
                  <td className="td-cell text-ui-text2">
                    {pref
                      ? t(`settings.materialService.materials.selectionMethods.${pref.selection_method}`, pref.selection_method)
                      : t('settings.materialService.materials.selectionMethods.LOWEST_COST', 'Lowest cost')}
                  </td>
                  <td className="td-cell text-right">
                    <Button type="button" variant="soft" size="xs" onClick={() => setPricingModalFor({ code: material.material_code, name: material.material_name })}>
                      {t('settings.materialService.materials.viewPricing', 'View pricing')}
                    </Button>
                    <Button type="button" variant="soft" size="xs" onClick={() => setEditingMaterialCode(material.material_code)} className="ml-1">
                      {t('common.edit', 'Edit')}
                    </Button>
                    {pref && (
                      <Button type="button" variant="destructive-ghost" size="xs" onClick={() => void remove(pref.tenant_material_preference_code)} className="ml-1">
                        {t('common.delete', 'Delete')}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="flex items-center justify-between text-xs text-ui-text2">
          <span>
            {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} {t('common.of', 'of')} {total}
            {isFetching && ` · ${t('settings.materialService.updating', 'updating…')}`}
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => setPage((previous) => Math.max(0, previous - 1))}
              disabled={page === 0}
            >
              {t('common.previous', 'Previous')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => setPage((previous) => Math.min(lastPage, previous + 1))}
              disabled={page >= lastPage}
            >
              {t('common.next', 'Next')}
            </Button>
          </div>
        </div>
      )}

      {pricingModalFor && (
        <MaterialPricingModal
          materialCode={pricingModalFor.code}
          materialName={pricingModalFor.name}
          onClose={() => setPricingModalFor(null)}
          onToast={onToast}
        />
      )}
    </div>
  );
};

export default MaterialsPreferenceSection;
